/**
 * Активный язык кабинета — один на приложение, снаружи React.
 *
 * Почему не только контекст: половина текстов кабинета рождается в императивном
 * коде — попапы MaplibreGL, подписи экспортируемых PNG/MP4, границы ошибок
 * (класс-компонент), обработчики карты. Хук туда не дотянуть, а таскать `t`
 * параметром через десяток слоёв значило бы поменять подписи всех этих функций.
 * Поэтому язык живёт в модуле, а React подписывается на него через
 * `useSyncExternalStore` — и провайдер, и попап видят одно и то же значение.
 *
 * Выбор до входа хранится в localStorage: экран входа рисуется раньше, чем
 * профиль вообще известен.
 */
import { catalogFor } from './catalog'
import { translate } from './translate'
import { FALLBACK_LANG } from './resolveLang'
import { isLang, type Lang, type Vars } from './types'

export const LANG_KEY = 'cabinet.lang'

let active: Lang = FALLBACK_LANG
const listeners = new Set<() => void>()

export function getLang(): Lang {
  return active
}

/** Подписка для useSyncExternalStore. */
export function subscribeLang(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Сменить язык приложения (без записи в localStorage — это делает setLang). */
export function applyLang(lang: Lang): void {
  if (active === lang) return
  active = lang
  if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', lang)
  for (const listener of [...listeners]) listener()
}

/** Выбор пользователя из localStorage; null — не выбирал. */
export function storedLang(): Lang | null {
  try {
    const raw = localStorage.getItem(LANG_KEY)
    return isLang(raw) ? raw : null
  } catch {
    return null
  }
}

/** Запомнить выбор и применить его. */
export function setLang(lang: Lang): void {
  try {
    localStorage.setItem(LANG_KEY, lang)
  } catch {
    /* ignore */
  }
  applyLang(lang)
}

/**
 * Текст по ключу на активном языке. Единственная точка входа для кода вне
 * React; в компонентах ровно то же самое отдаёт `useT()`.
 */
export function t(key: string, vars?: Vars): string {
  return translate(catalogFor(active), active, key, vars)
}

/** Языки браузера как список тегов (для resolveLang). */
export function navigatorLangs(): string[] {
  if (typeof navigator === 'undefined') return []
  const list = navigator.languages && navigator.languages.length
    ? navigator.languages
    : navigator.language
      ? [navigator.language]
      : []
  return [...list]
}
